/**
 * Button Component - Compatible avec styled-components
 * Utilisé pour les tests UI
 */

import React from 'react';
import styled from 'styled-components';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'ghost' | 'outline' | 'destructive';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  asChild?: boolean;
}

const StyledButton = styled.button<{ $variant?: string; $size?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  border: 1px solid transparent;

  ${props => {
    switch (props.$size) {
      case 'sm':
        return 'padding: 4px 12px; font-size: 12px;';
      case 'lg':
        return 'padding: 12px 24px; font-size: 16px;';
      case 'icon':
        return 'padding: 8px; width: 36px; height: 36px;';
      default:
        return 'padding: 8px 16px;';
    }
  }}

  ${props => {
    switch (props.$variant) {
      case 'ghost':
        return `
          background: transparent;
          color: #333;
          &:hover {
            background: #f5f5f5;
          }
        `;
      case 'outline':
        return `
          background: transparent;
          border-color: #d9d9d9;
          color: #333;
          &:hover {
            background: #f5f5f5;
            border-color: #999;
          }
        `;
      case 'destructive':
        return `
          background: #ff4d4f;
          color: white;
          &:hover {
            background: #ff7875;
          }
        `;
      default:
        return `
          background: #1890ff;
          color: white;
          &:hover {
            background: #40a9ff;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'default', size = 'default', asChild, className, ...props }, ref) => {
    return (
      <StyledButton
        ref={ref}
        $variant={variant}
        $size={size}
        className={className}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
